import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ThreatMonitoringDashboard = () => {
  const [selectedThreat, setSelectedThreat] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30);

  // Mock real-time threat data
  const [threats] = useState([
    {
      id: 'FIMI-2024-0921-001',
      title: 'Coordinated Disinformation Campaign - Eastern Border',
      severity: 'critical',
      status: 'active',
      region: 'Poland, Lithuania, Latvia',
      detectedAt: new Date(Date.now() - 1800000),
      lastUpdate: new Date(Date.now() - 300000),
      affectedPlatforms: ['Facebook', 'Twitter', 'Telegram'],
      fimiScore: 8.7,
      narratives: ['Border Security Threats', 'EU Migration Crisis'],
      assignedTeams: ['MoFA-PL', 'MoI-LT', 'EFCSN-Baltic'],
      escalationLevel: 3,
      responseActions: 12,
      publicEngagement: 45600
    },
    {
      id: 'FIMI-2024-0921-002',
      title: 'Election Interference Attempt - Germany',
      severity: 'high',
      status: 'monitoring',
      region: 'Germany, France',
      detectedAt: new Date(Date.now() - 3600000),
      lastUpdate: new Date(Date.now() - 900000),
      affectedPlatforms: ['TikTok', 'Instagram', 'YouTube'],
      fimiScore: 7.2,
      narratives: ['Election Fraud Claims', 'Democratic Legitimacy'],
      assignedTeams: ['MoI-DE', 'EFCSN-Central'],
      escalationLevel: 2,
      responseActions: 8,
      publicEngagement: 23400
    },
    {
      id: 'FIMI-2024-0921-003',
      title: 'Energy Infrastructure Narrative - Nordic Region',
      severity: 'medium',
      status: 'investigating',
      region: 'Sweden, Finland, Denmark',
      detectedAt: new Date(Date.now() - 7200000),
      lastUpdate: new Date(Date.now() - 1800000),
      affectedPlatforms: ['Facebook', 'LinkedIn', 'Reddit'],
      fimiScore: 5.8,
      narratives: ['Energy Security', 'Infrastructure Vulnerability'],
      assignedTeams: ['MoFA-SE', 'MoD-FI'],
      escalationLevel: 1,
      responseActions: 4,
      publicEngagement: 12800
    }
  ]);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'AlertTriangle';
      case 'monitoring': return 'Eye';
      case 'investigating': return 'Search';
      case 'resolved': return 'CheckCircle';
      default: return 'Circle';
    }
  };

  const formatTimeAgo = (date) => {
    const minutes = Math.floor((Date.now() - date?.getTime()) / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate real-time updates
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [refreshInterval]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Icon name="Shield" size={20} className="text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Real-Time Threat Monitoring</h2>
            <p className="text-sm text-gray-500">FIMI Detection & Response Dashboard</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-50 rounded-full">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-xs font-medium text-green-700">Live Monitoring</span>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => window.location?.reload()}
          >
            Refresh
          </Button>
        </div>
      </div>
      {/* Threat Overview Cards */}
      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Critical Threats</p>
                <p className="text-2xl font-bold text-red-700">1</p>
              </div>
              <Icon name="AlertTriangle" size={24} className="text-red-500" />
            </div>
          </div>
          
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">High Priority</p>
                <p className="text-2xl font-bold text-orange-700">1</p>
              </div>
              <Icon name="Eye" size={24} className="text-orange-500" />
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Active Teams</p>
                <p className="text-2xl font-bold text-blue-700">7</p>
              </div>
              <Icon name="Users" size={24} className="text-blue-500" />
            </div>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Resolved Today</p>
                <p className="text-2xl font-bold text-green-700">3</p>
              </div>
              <Icon name="CheckCircle" size={24} className="text-green-500" />
            </div>
          </div>
        </div>
      </div>
      {/* Active Threats List */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Active Incidents</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Auto-refresh:</span>
            <select 
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e?.target?.value))}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value={10}>10s</option>
              <option value={30}>30s</option>
              <option value={60}>1m</option>
            </select>
          </div>
        </div>

        <div className="space-y-4">
          {threats?.map((threat) => (
            <div 
              key={threat?.id}
              className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                selectedThreat?.id === threat?.id ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'
              }`}
              onClick={() => setSelectedThreat(threat)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSeverityColor(threat?.severity)}`}>
                      {threat?.severity?.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500 font-mono">{threat?.id}</span>
                    <div className="flex items-center space-x-1">
                      <Icon name={getStatusIcon(threat?.status)} size={14} className="text-gray-400" />
                      <span className="text-xs text-gray-500 capitalize">{threat?.status}</span>
                    </div>
                  </div>
                  
                  <h4 className="text-base font-medium text-gray-900 mb-2">{threat?.title}</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Region:</span> {threat?.region}
                    </div>
                    <div>
                      <span className="font-medium">FIMI Score:</span> 
                      <span className={`ml-1 font-bold ${
                        threat?.fimiScore >= 8 ? 'text-red-600' : 
                        threat?.fimiScore >= 6 ? 'text-orange-600' : 'text-yellow-600'
                      }`}>
                        {threat?.fimiScore}/10
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Engagement:</span> {threat?.publicEngagement?.toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>Detected: {formatTimeAgo(threat?.detectedAt)}</span>
                      <span>Updated: {formatTimeAgo(threat?.lastUpdate)}</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {threat?.assignedTeams?.slice(0, 3)?.map((team, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">
                          {team}
                        </span>
                      ))}
                      {threat?.assignedTeams?.length > 3 && (
                        <span className="text-xs text-gray-500">+{threat?.assignedTeams?.length - 3} more</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="ml-4 flex flex-col items-end space-y-2">
                  <div className={`w-3 h-3 rounded-full ${
                    threat?.escalationLevel >= 3 ? 'bg-red-500' :
                    threat?.escalationLevel >= 2 ? 'bg-orange-500' : 'bg-yellow-500'
                  }`} />
                  <span className="text-xs text-gray-500">Level {threat?.escalationLevel}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ThreatMonitoringDashboard;